import { useState } from "react";
import { Logo } from "@/components/Logo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { EASE } from "@/components/motion";

export const inputCls =
  "w-full border border-slate-200 rounded-xl p-3 md:p-4 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-800 bg-white transition";
export const btnPrimary =
  "inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-3 md:py-4 px-6 rounded-xl shadow-md hover:bg-blue-700 transform active:scale-95 transition disabled:opacity-60 disabled:pointer-events-none";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/portal";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white overflow-hidden">
        <img src="https://images.unsplash.com/photo-1579975194233-8a4c97b11f1b?auto=format&fit=crop&w=1600&q=75" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-slate-900/90 to-indigo-900/80" />
        <Link to="/" data-testid="auth-home-link" className="relative flex items-center"><Logo dark className="h-9" /></Link>
        <div className="relative">
          <p className="text-5xl xl:text-6xl font-black tracking-tighter leading-[0.95]">One more <span className="text-gradient">soul.</span></p>
          <p className="mt-5 text-slate-300 max-w-md">NIG adalah kunci akses utama Anda: login portal, absen ibadah via QR, daftar event, hingga layanan pastoral.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center mb-10"><Logo className="h-8" /></Link>
          <h1 className="text-4xl font-black tracking-tight text-slate-900">{title}</h1>
          <p className="mt-2 text-slate-500">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </motion.div>
      </div>
    </div>
  );
}

function GoogleButton() {
  return (
    <button type="button" onClick={googleLogin} data-testid="google-login-button" className="w-full flex items-center justify-center gap-3 border border-slate-200 bg-white rounded-xl py-3 md:py-4 font-semibold text-slate-700 hover:bg-slate-50 active:scale-95 transition">
      <svg viewBox="0 0 48 48" className="w-5 h-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      Lanjutkan dengan Google
    </button>
  );
}

const Divider = () => (
  <div className="my-6 flex items-center gap-4 text-xs font-semibold text-slate-400 uppercase tracking-widest"><span className="h-px flex-1 bg-slate-200" />atau<span className="h-px flex-1 bg-slate-200" /></div>
);

function ForgotPassword() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ identifier: "", note: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", form);
      toast.success("Permintaan terkirim. Admin Pastoral akan menghubungi Anda dengan password baru.");
      setOpen(false);
      setForm({ identifier: "", note: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="forgot-password-link" className="text-sm font-bold text-blue-600 hover:text-blue-700">Lupa password?</button>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="forgot-password-dialog">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl">
            <h2 className="text-xl font-black text-slate-900">Lupa Password</h2>
            <p className="mt-1 text-sm text-slate-500">Permintaan reset akan dikirim ke Admin Pastoral. Anda akan dihubungi dengan password baru.</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input data-testid="forgot-identifier-input" className={inputCls} placeholder="Email atau NIG" value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} required />
              <input data-testid="forgot-note-input" className={inputCls} placeholder="Catatan (opsional, mis. no. WA aktif)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              <button data-testid="forgot-submit-button" disabled={loading} className={`${btnPrimary} w-full`}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kirim Permintaan"}</button>
              <button type="button" onClick={() => setOpen(false)} data-testid="forgot-cancel-button" className="w-full text-sm font-bold text-slate-500 py-2">Batal</button>
            </form>
          </motion.div>
        </div>
      )}
    </>
  );
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(form.identifier, form.password);
      navigate(u.perms?.length ? "/admin" : (location.state?.from || "/portal"), { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Masuk Portal" subtitle="Gunakan Email atau NIG Anda.">
      <GoogleButton />
      <Divider />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <input data-testid="login-identifier-input" className={inputCls} placeholder="Email atau NIG (contoh 26091001)" value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} required />
        <input data-testid="login-password-input" type="password" className={inputCls} placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        <button data-testid="login-submit-button" disabled={loading} className={`${btnPrimary} w-full`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Masuk <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">Belum punya NIG? <Link to="/register" data-testid="goto-register-link" className="font-bold text-blue-600 hover:text-blue-700">Daftar sekarang</Link></p>
        <ForgotPassword />
      </div>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", gender: "L", phone: "", birth_date: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, birth_date: form.birth_date || null, phone: form.phone || null });
      toast.success("Akun dibuat! Cek email Anda untuk verifikasi, lalu tunggu persetujuan admin.");
      navigate("/portal", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Daftar Jemaat Baru" subtitle="Daftar, verifikasi email, lalu tunggu persetujuan admin untuk mendapat NIG.">
      <GoogleButton />
      <Divider />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <input data-testid="register-name-input" className={inputCls} placeholder="Nama lengkap" value={form.name} onChange={set("name")} required />
        <input data-testid="register-email-input" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} required />
        <input data-testid="register-password-input" type="password" minLength={6} className={inputCls} placeholder="Password (min. 6 karakter)" value={form.password} onChange={set("password")} required />
        <div className="grid grid-cols-2 gap-3" data-testid="register-gender-group">
          {[["L", "Pria"], ["P", "Wanita"]].map(([v, l]) => (
            <button type="button" key={v} data-testid={`register-gender-${v}`} onClick={() => setForm({ ...form, gender: v })} className={`rounded-xl py-3 font-bold border transition active:scale-95 ${form.gender === v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="register-phone-input" className={inputCls} placeholder="No. WhatsApp" value={form.phone} onChange={set("phone")} />
          <input data-testid="register-birth-input" type="date" className={inputCls} value={form.birth_date} onChange={set("birth_date")} />
        </div>
        <button data-testid="register-submit-button" disabled={loading} className={`${btnPrimary} w-full`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Buat Akun <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>
      <p className="mt-6 text-sm text-slate-500">Sudah punya akun? <Link to="/login" data-testid="goto-login-link" className="font-bold text-blue-600 hover:text-blue-700">Masuk</Link></p>
    </AuthShell>
  );
}
